import { useUserInfo } from '@api/user/useUserInfo';
import { FeedbackSurvey } from '@components/FeedbackSurvey';
import { LoginError } from '@components/LoginError';
import {
  ContentText,
  IconInfo,
  Notification,
  spacing,
} from '@fortum/elemental-ui';
import { useTabsWithRouter } from '@utils/hooks/useTabsWithRouter';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { SkipToContentLink } from '@utils/accessibility/SkipToContentLink';
import { NotificationTextWithLink } from '@components/NotificationTextWithLink';
import { AppHeaderWrapper } from '@components/AppHeader/AppHeaderWrapper';
import { AppFooter } from '@components/FooterTemplate/AppFooter';
import {
  MainContainer,
  PositionedLoader,
  PositionedLoaderContainer,
  StyledMain,
  TopContainer,
} from './App.styles';
import { fallbackLng, isSupportedLanguage, Namespace } from './configs/i18n';

// Caps how long we'll mask the page behind a loader while waiting to find
// out whether the session is actually valid. This is not "wait for
// useUserInfo to finish" (that can take seconds and would slow down every
// load) - it's a small fixed ceiling so a fast 401 never flashes the
// authenticated shell first, while a slow one costs no more than this.
const AUTH_CHECK_GRACE_PERIOD_MS = 250;

export const AppMainLayout: React.FC = () => {
  const { t: glossaryT, i18n } = useTranslation<Namespace>('glossary');

  const [isNotificationClosed, setIsNotificationClosed] = useState(false);

  const language = useMemo(
    () => (isSupportedLanguage(i18n.language) ? i18n.language : fallbackLng),
    [i18n.language],
  );

  const notificationText = useMemo(() => glossaryT('notification'), [language]);
  const notificationLinkUrl = useMemo(
    () => glossaryT('notification.link.url'),
    [language],
  );
  const notificationLinkText = useMemo(
    () => glossaryT('notification.link.text'),
    [language],
  );
  const onLanguageSelect = async (lang: string) => {
    const supportedLanguage = isSupportedLanguage(lang) ? lang : fallbackLng;

    await i18n.changeLanguage(supportedLanguage);
  };

  const userInfoQuery = useUserInfo();

  const { tabItems, onTabChange, activeTab } = useTabsWithRouter();

  const [isAuthCheckGracePeriodOver, setIsAuthCheckGracePeriodOver] =
    useState(false);

  useEffect(() => {
    const timer = setTimeout(
      () => setIsAuthCheckGracePeriodOver(true),
      AUTH_CHECK_GRACE_PERIOD_MS,
    );

    return () => clearTimeout(timer);
  }, []);

  const isAwaitingAuthCheck =
    userInfoQuery.isLoading && !isAuthCheckGracePeriodOver;

  let mainContent;
  if (userInfoQuery.isError) {
    mainContent = <LoginError />;
  } else if (isAwaitingAuthCheck) {
    mainContent = (
      <PositionedLoaderContainer>
        <PositionedLoader size={48} />
      </PositionedLoaderContainer>
    );
  } else {
    mainContent = (
      <>
        {!isNotificationClosed && notificationText !== '-' && (
          <Notification
            data-test="notification-error"
            status="alert"
            icon={IconInfo}
            mb={spacing.xxxs}
            maxHeight="auto"
            onClose={() => setIsNotificationClosed(true)}
          >
            {notificationLinkText !== '-' && notificationLinkUrl !== '-' ? (
              <NotificationTextWithLink
                notificationText={notificationText}
                notificationLinkText={notificationLinkText}
                notificationLinkUrl={notificationLinkUrl}
              />
            ) : (
              <ContentText>{notificationText}</ContentText>
            )}
          </Notification>
        )}
        <StyledMain>
          <Suspense
            fallback={
              <PositionedLoaderContainer>
                <PositionedLoader size={48} />
              </PositionedLoaderContainer>
            }
          >
            <Outlet />
          </Suspense>
        </StyledMain>
      </>
    );
  }

  return (
    <MainContainer>
      <SkipToContentLink />
      <TopContainer>
        <AppHeaderWrapper
          onLanguageSelect={onLanguageSelect}
          navItems={tabItems}
          onNavSelect={onTabChange}
          activeNavItem={activeTab}
        />
        {mainContent}
      </TopContainer>
      <FeedbackSurvey />
      <AppFooter language={language} />
    </MainContainer>
  );
};
